import { WarningFilled } from '@ant-design/icons';
import { type CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Radio, Switch } from 'antd5';
import { clone, isEqual, startCase, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  CustomFilterContainer,
  CustomFilterPopover,
  SecondaryFilterLabel,
} from 'components/data/helpers/filters.style';
import { Button, Tooltip, TooltipWithQuestionIcon } from 'components/ui';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { AtlasGqlTaskStatus } from 'types/atlas-graphql';
import { TABLE_FILTER_OPERATORS } from 'utils/constants';

import { AdvancedFiltersCollapse, SwitchContainer } from './TasksFilter.style';

const noneValue = ' ';
const hasTasksValue = [1];
const noTasksValue = [TABLE_FILTER_OPERATORS.EQUALS, '0'];

enum damageTaskTypes {
  RepairTask = 'REPAIR_TASK',
  InspectTask = 'INSPECT_TASK',
}
const allDamageTaskTypes = Object.values(damageTaskTypes);
const allTaskStatuses = Object.values(AtlasGqlTaskStatus);

export const TasksFilter: React.FunctionComponent<CustomFilterDropdownProps> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const getFilterValueForKey = (k: string) => selectedKeys.find(({ key }) => key === k)?.value;

  const {
    taskCountValue,
    taskTypeValue,
    taskStatusValue,
    hasUnarchivedTasksValue,
    hasPrimaryGroupTasksValue,
  } = useMemo(
    () => ({
      taskCountValue: getFilterValueForKey('taskCount'),
      taskTypeValue: getFilterValueForKey('taskType'),
      taskStatusValue: getFilterValueForKey('taskStatus'),
      hasUnarchivedTasksValue: getFilterValueForKey('hasUnarchivedTasks'),
      hasPrimaryGroupTasksValue: getFilterValueForKey('hasPrimaryGroupTasks'),
    }),
    [selectedKeys]
  );
  const advancedFiltersDisabled: boolean = !isEqual(taskCountValue, hasTasksValue);

  const getToggleHandler =
    (filterKey: string, allOptions: string[]) => (option: string, checked: boolean) => {
      /**
       * previously included options is the existing filter, if it exists,
       * otherwise, all options were previously included
       */
      let includedOptions = (getFilterValueForKey(filterKey) as string[]) ?? allOptions;

      if (checked) {
        includedOptions = uniq([...includedOptions, option]);
      } else if (!checked) {
        includedOptions = includedOptions.filter(o => o !== option);
      }

      const otherExistingFilters = selectedKeys.filter(({ key }) => key !== filterKey);
      /**
       * if every option should be included, don't send a filter (i.e. this
       * is the default), otherwise, set the included options as the filter
       */
      if (allOptions.every(o => includedOptions.includes(o))) {
        setSelectedKeys(otherExistingFilters);
      } else {
        setSelectedKeys([...otherExistingFilters, { key: filterKey, value: includedOptions }]);
      }
    };

  const handleToggleType = useCallback(getToggleHandler('taskType', allDamageTaskTypes), [
    allDamageTaskTypes,
    selectedKeys,
    setSelectedKeys,
  ]);
  const handleToggleStatus = useCallback(getToggleHandler('taskStatus', allTaskStatuses), [
    allTaskStatuses,
    selectedKeys,
    setSelectedKeys,
  ]);

  const taskTypeLabelMap: Record<string, string> = {
    [damageTaskTypes.RepairTask]: t('tasks.repairTasksLabel', 'Repair Tasks'),
    [damageTaskTypes.InspectTask]: t('tasks.inspectTasksLabel', 'Inspect Tasks'),
  };

  return (
    <CustomFilterPopover className="ant-table-filter-dropdown" $width="340px">
      <CustomFilterContainer>
        <SecondaryFilterLabel>{t('table.tasks', 'Tasks')}</SecondaryFilterLabel>
        <Radio.Group
          options={[
            { label: t('table.tasks', 'Tasks'), value: true },
            { label: t('tasks.noTasks', 'No Tasks'), value: false },
            { label: t('table.all', 'All'), value: noneValue },
          ]}
          onChange={({ target }: CheckboxChangeEvent) => {
            if (target.value === true) {
              setSelectedKeys([
                ...selectedKeys.filter(({ key }) => key !== 'taskCount'),
                { key: 'taskCount', value: hasTasksValue },
              ]);
            } else if (target.value === false) {
              setSelectedKeys([{ key: 'taskCount', value: noTasksValue }]);
            } else {
              setSelectedKeys([]);
            }
          }}
          value={
            isEqual(taskCountValue, hasTasksValue)
              ? true
              : isEqual(taskCountValue, noTasksValue)
                ? false
                : noneValue
          }
          optionType="button"
        />
      </CustomFilterContainer>

      <AdvancedFiltersCollapse
        defaultActiveKey={
          !!taskTypeValue ||
          !!taskStatusValue ||
          !!hasPrimaryGroupTasksValue ||
          !!hasUnarchivedTasksValue
            ? 1
            : undefined
        }
        items={[
          {
            key: 1,
            label: (
              <>
                <span>{t('tasks.advancedTaskFilters', 'Advanced Task Filters')}</span>{' '}
                <TooltipWithQuestionIcon
                  message={t(
                    'tasks.advancedTaskFiltersTooltip',
                    'When "Tasks" is selected above, Advanced Task Filters may be used for further refinement'
                  )}
                />
              </>
            ),
            children: (
              <>
                <CustomFilterContainer>
                  <SecondaryFilterLabel>
                    {t('tasks.taskTypes', 'Task Types')}{' '}
                    {isEqual(taskTypeValue, []) && (
                      <Tooltip
                        title={t(
                          'tasks.toggleAllSwitchesWarning',
                          'Turning off all the switches in this section will hide all table entries'
                        )}
                      >
                        <WarningFilled style={{ color: theme.severityHighColor }} />
                      </Tooltip>
                    )}
                  </SecondaryFilterLabel>
                  {allDamageTaskTypes.map(type => (
                    <SwitchContainer key={type} $disabled={advancedFiltersDisabled}>
                      {taskTypeLabelMap[type]}:{' '}
                      <Switch
                        onChange={checked => handleToggleType(type, checked)}
                        checked={!taskTypeValue || taskTypeValue.includes(type)}
                        disabled={advancedFiltersDisabled}
                      />
                    </SwitchContainer>
                  ))}
                </CustomFilterContainer>
                <CustomFilterContainer>
                  <SecondaryFilterLabel>
                    {t('tasks.taskStatus', 'Task Status')}{' '}
                    {isEqual(taskStatusValue, []) && (
                      <Tooltip
                        title={t(
                          'tasks.toggleAllSwitchesWarning',
                          'Turning off all the switches in this section will hide all table entries'
                        )}
                      >
                        <WarningFilled style={{ color: theme.severityHighColor }} />
                      </Tooltip>
                    )}
                  </SecondaryFilterLabel>
                  {clone(allTaskStatuses)
                    .reverse()
                    .map(status => (
                      <SwitchContainer key={status} $disabled={advancedFiltersDisabled}>
                        {startCase(status.toLowerCase())}:{' '}
                        <Switch
                          onChange={checked => handleToggleStatus(status, checked)}
                          checked={!taskStatusValue || taskStatusValue.includes(status)}
                          disabled={advancedFiltersDisabled}
                        />
                      </SwitchContainer>
                    ))}
                </CustomFilterContainer>
                <CustomFilterContainer>
                  <SecondaryFilterLabel>
                    {t('workOrder.status.archived', 'Archived')}{' '}
                    <TooltipWithQuestionIcon
                      message={t(
                        'tasks.archivedTooltip',
                        'Show or hide damages where the only related tasks are archived'
                      )}
                    />
                  </SecondaryFilterLabel>
                  <Radio.Group
                    options={[
                      { label: t('workOrder.active', 'Active'), value: true },
                      { label: t('workOrder.status.archived', 'Archived'), value: false },
                      { label: t('tasks.both', 'Both'), value: noneValue },
                    ]}
                    onChange={({ target }: CheckboxChangeEvent) => {
                      if (target.value === true) {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasUnarchivedTasks'),
                          { key: 'hasUnarchivedTasks', value: 'true' },
                        ]);
                      } else if (target.value === false) {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasUnarchivedTasks'),
                          { key: 'hasUnarchivedTasks', value: 'false' },
                        ]);
                      } else {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasUnarchivedTasks'),
                        ]);
                      }
                    }}
                    value={
                      hasUnarchivedTasksValue === 'true'
                        ? true
                        : hasUnarchivedTasksValue === 'false'
                          ? false
                          : noneValue
                    }
                    disabled={advancedFiltersDisabled}
                    optionType="button"
                  />
                </CustomFilterContainer>
                <CustomFilterContainer>
                  <SecondaryFilterLabel>
                    {t('tasks.basedOnPrimary', 'Based on Primary')}{' '}
                    <TooltipWithQuestionIcon
                      message={t(
                        'tasks.basedOnPrimaryTooltip',
                        'Show or hide damages with tasks created against non-Primary Observation Groups'
                      )}
                    />
                  </SecondaryFilterLabel>
                  <Radio.Group
                    options={[
                      { label: t('tasks.primary', 'Primary'), value: true },
                      { label: t('tasks.nonPrimary', 'Non-Primary'), value: false },
                      { label: t('tasks.both', 'Both'), value: noneValue },
                    ]}
                    onChange={({ target }: CheckboxChangeEvent) => {
                      if (target.value === true) {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasPrimaryGroupTasks'),
                          { key: 'hasPrimaryGroupTasks', value: 'true' },
                        ]);
                      } else if (target.value === false) {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasPrimaryGroupTasks'),
                          { key: 'hasPrimaryGroupTasks', value: 'false' },
                        ]);
                      } else {
                        setSelectedKeys([
                          ...selectedKeys.filter(({ key }) => key !== 'hasPrimaryGroupTasks'),
                        ]);
                      }
                    }}
                    value={
                      hasPrimaryGroupTasksValue === 'true'
                        ? true
                        : hasPrimaryGroupTasksValue === 'false'
                          ? false
                          : noneValue
                    }
                    disabled={advancedFiltersDisabled}
                    optionType="button"
                  />
                </CustomFilterContainer>
              </>
            ),
          },
        ]}
      />

      <div className="ant-table-filter-dropdown-btns" style={{ borderTop: '1px solid #f0f0f0' }}>
        <Button type="link" size="small" onClick={clearFilters}>
          {t('table.reset', 'Reset')}
        </Button>
        <Button type="primary" size="small" onClick={() => confirm({ closeDropdown: true })}>
          {t('table.ok', 'OK')}
        </Button>
      </div>
    </CustomFilterPopover>
  );
};
